import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowDownIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ClockIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import QuestionCard from '../components/questions/QuestionCard';
import GameProgressPanel from '../components/game/GameProgressPanel';
import PersonalStatsSection from '../components/stats/PersonalStatsSection';
import PersonalBestsSection from '../components/stats/PersonalBestsSection';
import { Dropdown, DropdownItem } from '../components/ui/Dropdown';
import { QuestionApiModel } from '../types/questions';
import { AttemptResult } from '../types/practice';

type PreviewTab = 'practice' | 'game-modes' | 'stats';

const previewTabs: { key: PreviewTab; label: string }[] = [
  { key: 'practice', label: 'Practice Mode' },
  { key: 'game-modes', label: 'Game Modes' },
  { key: 'stats', label: 'Stats & Rankings' },
];

const sampleQuestion: QuestionApiModel = {
  id: 'landing-sample-question',
  year: 2023,
  exam_type: 2,
  question_number: 7,
  sub_question: '',
  question_type: 'MC',
  vce_area_of_study: 'Calculus',
  question_text: "If $f(x)=x^2-4x+1$, what is $f'(3)$?",
  option_a: '$1$',
  option_b: '$2$',
  option_c: '$3$',
  option_d: '$6$',
  option_e: null,
  correct_answer: 'B',
  difficulty_rating: 24,
  marks: 1,
  worked_solution: "Differentiate first: $f'(x)=2x-4$. Substituting $x=3$ gives $f'(3)=2$.",
  tags: [
    { id: 'derivatives', name: 'Derivatives' },
    { id: 'calculus', name: 'Calculus' },
  ],
  progress: null,
};

const sampleAttemptHistory = [
  { question_number: 1, is_correct: true, difficulty_rating: 18 },
  { question_number: 2, is_correct: true, difficulty_rating: 21 },
  { question_number: 3, is_correct: false, difficulty_rating: 25 },
  { question_number: 4, is_correct: true, difficulty_rating: 19 },
  { question_number: 5, is_correct: true, difficulty_rating: 27 },
  { question_number: 6, is_correct: true, difficulty_rating: 29 },
];

const sampleUserStats = {
  total_questions_answered: 184,
  correct_answers: 136,
  accuracy_percentage: 73.9,
  current_streak: 12,
  current_rating: 28.4,
};

const samplePersonalBests = {
  survival_best: {
    score: 14,
    date: '2026-04-01T10:00:00Z',
  },
  rush_5_best: {
    score: 17,
    date: '2026-04-02T10:00:00Z',
    duration_minutes: 5,
  },
  rush_10_best: {
    score: 28,
    date: '2026-04-03T10:00:00Z',
    duration_minutes: 10,
  },
  rush_20_best: {
    score: 41,
    date: '2026-03-30T10:00:00Z',
    duration_minutes: 20,
  },
  rush_30_best: {
    score: 58,
    date: '2026-03-27T10:00:00Z',
    duration_minutes: 30,
  },
};

const studyTools = [
  {
    title: 'Question Library',
    description: 'Browse questions by year and question number for reference',
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    iconPath:
      'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  },
  {
    title: 'Practice by Tags',
    description: 'Build a custom session by selecting specific concepts',
    iconBg: 'bg-mango-100',
    iconColor: 'text-mango-600',
    iconPath:
      'M7 7h.01M4 4l7.586-1.172a2 2 0 011.414.414l7.758 7.758a2 2 0 010 2.828l-6.364 6.364a2 2 0 01-2.828 0l-7.758-7.758a2 2 0 01-.414-1.414L4 4z',
  },
  {
    title: 'Stats & Rankings',
    description: 'View your performance and global leaderboards',
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    iconPath:
      'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  },
];

const Header: React.FC = () => {
  const navigate = useNavigate();

  const trigger = (
    <button
      type="button"
      aria-label="Open account menu"
      className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white/90 p-2.5 text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900"
    >
      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
    </button>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200/80 bg-gray-50/85 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-mango-500 rounded-lg flex items-center justify-center">
              <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
              </svg>
            </div>
            <div className="ml-3">
              <h1 className="text-2xl font-bold text-gray-900">Cracked At</h1>
            </div>
          </div>

          <Dropdown trigger={trigger} align="right">
            <DropdownItem onClick={() => navigate('/login')}>
              Log in
            </DropdownItem>
            <DropdownItem onClick={() => navigate('/register')}>
              Create account
            </DropdownItem>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};

const HERO_SUBJECTS = ['Methods', 'Specialist Maths'];

type Typing = { index: number; length: number; deleting: boolean };

const useTypedSubject = (subjects: string[]) => {
  const [reduceMotion] = React.useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const [typing, setTyping] = React.useState<Typing>({ index: 0, length: 0, deleting: false });

  React.useEffect(() => {
    if (reduceMotion) return;
    const { index, length, deleting } = typing;
    const typed = !deleting && length === subjects[index].length;
    const erased = deleting && length === 0;
    const [delay, next]: [number, Typing] = typed
      ? [1600, { index, length, deleting: true }]
      : erased
        ? [300, { index: (index + 1) % subjects.length, length: 0, deleting: false }]
        : [deleting ? 45 : 90, { index, length: length + (deleting ? -1 : 1), deleting }];
    const timer = window.setTimeout(() => setTyping(next), delay);
    return () => window.clearTimeout(timer);
  }, [typing, reduceMotion, subjects]);

  return {
    text: reduceMotion ? subjects[0] : subjects[typing.index].slice(0, typing.length),
    animated: !reduceMotion,
  };
};

const HeroSection: React.FC = () => {
  const subject = useTypedSubject(HERO_SUBJECTS);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
      <div className="mx-auto max-w-4xl text-center">
        <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
          <span className="sr-only">Want to get Cracked At {HERO_SUBJECTS.join(' or ')}?</span>
          <span aria-hidden="true">
            <span className="block">Want to get</span>
            <span className="block">Cracked At</span>
            <span className="block min-h-[1.25em] whitespace-nowrap text-mango-500">
              {subject.text}
              {subject.animated && (
                <span className="-mr-[3px] inline-block h-[0.9em] w-[3px] translate-y-[0.1em] bg-current animate-blink" />
              )}
              ?
            </span>
          </span>
        </h2>
        <p className="mt-5 mx-auto max-w-2xl text-lg text-gray-600 leading-8">
          A practice tool that simplifies and accelerates your VCE maths preparation
        </p>

        <div className="mt-12 flex justify-center">
          <a
            href="#platform-preview"
            aria-label="Scroll to platform preview"
            className="group flex flex-col items-center justify-center text-gray-400 transition-colors hover:text-mango-600"
          >
            <span className="h-16 w-px bg-gradient-to-b from-transparent via-gray-300 to-gray-300 group-hover:via-mango-400 group-hover:to-mango-500" />
            <ArrowDownIcon className="mt-4 h-10 w-10 text-mango-500 transition-transform group-hover:translate-y-1" />
          </a>
        </div>
      </div>
    </section>
  );
};

const PracticePreview: React.FC = () => {
  const [attemptResult, setAttemptResult] = React.useState<AttemptResult | null>(null);
  const [questionKey, setQuestionKey] = React.useState(0);

  const handleAnswer = async (selectedAnswer: string) => {
    const isCorrect = selectedAnswer === sampleQuestion.correct_answer;
    setAttemptResult({
      is_correct: isCorrect,
      rating_before: 24.8,
      rating_after: isCorrect ? 26.1 : 24.0,
      rating_change: isCorrect ? 1.3 : -0.8,
      correct_answer: sampleQuestion.correct_answer,
    });
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900">Practice Mode</h3>
        <p className="mt-2 text-sm text-gray-600">
          Answer a sample question and open the worked solution to preview the question flow.
        </p>
      </div>

      <QuestionCard
        key={questionKey}
        question={sampleQuestion}
        onAnswer={handleAnswer}
        attemptResult={attemptResult}
        workedSolution={sampleQuestion.worked_solution ?? undefined}
      />

      {attemptResult && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => {
              setAttemptResult(null);
              setQuestionKey((value) => value + 1);
            }}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900"
          >
            Reset preview
          </button>
        </div>
      )}
    </div>
  );
};

const GameModesPreview: React.FC = () => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900">Game Modes</h3>
        <p className="mt-2 text-sm text-gray-600">
          Survival Mode tracks your streak. Rush Mode gives timed challenges with multiple durations.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-medium text-gray-900">Game Modes</h4>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="block p-4 border border-gray-200 rounded-lg text-left">
              <div className="flex items-center mb-2">
                <div className="p-1 bg-red-100 rounded">
                  <HeartIcon className="h-5 w-5 text-red-600" />
                </div>
                <h5 className="ml-2 font-medium text-gray-900">Survival Mode</h5>
              </div>
              <p className="text-sm text-gray-600">How many can you get right in a row?</p>
            </div>

            <div className="block p-4 border border-gray-200 rounded-lg text-left">
              <div className="flex items-center mb-2">
                <div className="p-1 bg-blue-100 rounded">
                  <ClockIcon className="h-5 w-5 text-blue-600" />
                </div>
                <h5 className="ml-2 font-medium text-gray-900">Rush Mode</h5>
              </div>
              <p className="text-sm text-gray-600">Timed challenges with multiple durations</p>
            </div>
          </div>
        </div>
      </div>

      <GameProgressPanel
        attemptHistory={sampleAttemptHistory}
        currentQuestionNumber={7}
        gameMode="survival"
        lives={2}
        maxLives={3}
      />
    </div>
  );
};

const StatsPreview: React.FC = () => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900">Stats & Rankings</h3>
        <p className="mt-2 text-sm text-gray-600">
          Review your current rating, questions solved, streak, accuracy, and personal bests.
        </p>
      </div>

      <div className="grid gap-6">
        <PersonalStatsSection userStats={sampleUserStats} isLoading={false} />
        <PersonalBestsSection personalBests={samplePersonalBests} isLoading={false} />
      </div>
    </div>
  );
};

const PlatformPreview: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<PreviewTab>('practice');

  return (
    <section id="platform-preview" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="bg-white rounded-2xl shadow">
        <div className="px-6 py-6 border-b border-gray-200 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Try the features</h2>
        </div>

        <div className="px-6 pt-6">
          <div className="flex flex-wrap gap-3">
            {previewTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-mango-500 text-white'
                    : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'practice' && <PracticePreview />}
          {activeTab === 'game-modes' && <GameModesPreview />}
          {activeTab === 'stats' && <StatsPreview />}
        </div>
      </div>
    </section>
  );
};

const StudyToolsSection: React.FC = () => {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-2xl shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Study Tools</h2>
          <p className="mt-1 text-sm text-gray-600">
            These tools are available inside the platform.
          </p>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {studyTools.map((tool) => (
            <div
              key={tool.title}
              className="block p-4 border border-gray-200 rounded-lg transition-colors hover:border-mango-500 hover:bg-mango-50"
            >
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${tool.iconBg}`}>
                  <svg className={`h-6 w-6 ${tool.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tool.iconPath} />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="font-medium text-gray-900">{tool.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{tool.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FooterCallToAction: React.FC = () => {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-10">
      <div className="bg-white rounded-2xl shadow p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Start practicing in Cracked At</h2>
            <p className="mt-2 text-sm text-gray-600">
              Create an account to save progress and use the full platform.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-mango-500 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-mango-600"
            >
              Create account
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-5 py-3 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900"
            >
              Log in
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <HeroSection />
      <PlatformPreview />
      <StudyToolsSection />
      <FooterCallToAction />
    </div>
  );
};

export default LandingPage;
